// LearnCore Frontend - 富文本渲染组件


interface RichTextRendererProps {
  content: string;
  className?: string;
  /** 是否解析HTML标签，默认true */
  parseHtml?: boolean;
  /** 是否解析Markdown语法，默认true */
  parseMarkdown?: boolean;
  /** 是否显示填空挖空，默认false（显示答案） */
  showCloze?: boolean;
}

/**
 * 富文本渲染组件
 * 支持HTML行级内联标签和Markdown基本语法
 */
export default function RichTextRenderer({ 
  content, 
  className = '', 
  parseHtml = true, 
  parseMarkdown = true,
  showCloze = false
}: RichTextRendererProps) {
  
  /**
   * 解析填空标记 {{c1::答案}}
   * 支持: {{c1::答案}}, {{c2::答案}} 等格式
   */
  const parseClozeNotation = (text: string): string => {
    if (!parseHtml) return text;
    
    // 匹配填空标记：{{c数字::内容}}
    const clozeRegex = /\{\{c(\d+)::([^}]+)\}\}/g;
    
    return text.replace(clozeRegex, (_, __, content) => {
      if (showCloze) {
        // 显示挖空状态
        return `<span class="cloze-blank inline-block px-2 py-1 mx-1 bg-gray-200 border-b-2 border-dashed border-gray-400 text-gray-200 font-mono text-sm">______</span>`;
      } else {
        // 显示答案
        return `<span class="cloze-answer inline-block px-2 py-1 mx-1 bg-green-100 border border-green-300 text-green-800 font-medium rounded">${content}</span>`;
      }
    });
  };

  /**
   * 解析HTML行级内联标签
   * 支持: <strong>, <em>, <i>, <u>, <sub>, <sup>
   */
  const parseHtmlTags = (text: string): string => {
    if (!parseHtml) return text;
    
    // 安全的HTML标签替换
    const htmlTagReplacements = [
      // <strong>加粗文本</strong>
      {
        pattern: /<strong>(.*?)<\/strong>/gi,
        replacement: '<strong>$1</strong>'
      },
      // <em>强调文本</em>
      {
        pattern: /<em>(.*?)<\/em>/gi,
        replacement: '<em>$1</em>'
      },
      // <i>斜体文本</i>
      {
        pattern: /<i>(.*?)<\/i>/gi,
        replacement: '<i>$1</i>'
      },
      // <u>下划线文本</u>
      {
        pattern: /<u>(.*?)<\/u>/gi,
        replacement: '<u>$1</u>'
      },
      // <sub>下标文本</sub>
      {
        pattern: /<sub>(.*?)<\/sub>/gi,
        replacement: '<sub>$1</sub>'
      },
      // <sup>上标文本</sup>
      {
        pattern: /<sup>(.*?)<\/sup>/gi,
        replacement: '<sup>$1</sup>'
      }
    ];

    let result = text;
    for (const { pattern, replacement } of htmlTagReplacements) {
      result = result.replace(pattern, replacement);
    }
    
    return result;
  };

  /**
   * 解析Markdown语法
   * 支持: 标题(#), 强调(**粗体**, *斜体*), 有序列表(1.), 无序列表(- *), 引用(>)
   */
  const parseMarkdownSyntax = (text: string): string => {
    if (!parseMarkdown) return text;
    
    let result = text;

    // 1. 标题语法 (# ## ### #### ##### ######)
    result = result.replace(/^(#{1,6})\s+(.*?)$/gm, (_, hashes, content) => {
      const level = hashes.length;
      return `<h${level}>${content.trim()}</h${level}>`;
    });

    // 2. 强调语法 - 分步骤处理避免冲突
    // 首先处理 **粗体** 和 __粗体__
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    result = result.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // 然后处理 *斜体* - 使用简单方法避免浏览器兼容性问题
    result = result.replace(/\*([^*\n]+?)\*/g, (match, content, offset, string) => {
      // 检查是否是双星号的一部分
      const before = string[offset - 1];
      const after = string[offset + match.length];
      if (before === '*' || after === '*') {
        return match; // 保持原样
      }
      return `<em>${content}</em>`;
    });
    
    // 处理 _斜体_ - 使用占位符方法避免冲突
    const PLACEHOLDER = '___TEMP_UNDERSCORE___';
    // 先保护已经处理的双下划线粗体
    result = result.replace(/<strong>(.*?)<\/strong>/g, (match) => {
      return match.replace(/_/g, PLACEHOLDER);
    });
    // 处理单下划线斜体
    result = result.replace(/_([^_\n]+?)_/g, '<em>$1</em>');
    // 恢复占位符
    result = result.replace(new RegExp(PLACEHOLDER, 'g'), '_');

    // 3. 有序列表 (数字 + 英文句号)
    result = result.replace(/^(\d+)\.\s+(.*?)$/gm, '<li class="ordered-list-item">$2</li>');
    
    // 4. 无序列表 (- 或 *)
    result = result.replace(/^[-*]\s+(.*?)$/gm, '<li class="unordered-list-item">$2</li>');
    
    // 5. 引用语法 (>)
    result = result.replace(/^>\s+(.*?)$/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-600">$1</blockquote>');

    return result;
  };

  /**
   * 处理换行符
   */
  const handleLineBreaks = (text: string): string => {
    return text.replace(/\n/g, '<br>');
  };

  /**
   * 包装列表项为完整的列表
   */
  const wrapListItems = (text: string): string => {
    // 包装连续的有序列表项
    text = text.replace(
      /(<li class="ordered-list-item">.*?<\/li>)(\s*<li class="ordered-list-item">.*?<\/li>)*/g,
      (match) => `<ol>${match}</ol>`
    );
    
    // 包装连续的无序列表项
    text = text.replace(
      /(<li class="unordered-list-item">.*?<\/li>)(\s*<li class="unordered-list-item">.*?<\/li>)*/g,
      (match) => `<ul>${match}</ul>`
    );
    
    return text;
  };

  // 处理文本内容
  const processContent = (text: string): string => {
    let processed = text;
    
    // 1. 解析填空标记（优先处理，避免被其他解析影响）
    processed = parseClozeNotation(processed);
    
    // 2. 解析Markdown语法
    processed = parseMarkdownSyntax(processed);
    
    // 3. 解析HTML标签
    processed = parseHtmlTags(processed);
    
    // 4. 包装列表项
    processed = wrapListItems(processed);
    
    // 5. 处理换行符
    processed = handleLineBreaks(processed);
    
    return processed;
  };

  const processedContent = processContent(content);

  return (
    <div 
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
}

/**
 * 简化的文本渲染器，仅支持基本的强调语法
 * 适用于简短的内容，如卡片标题等
 */
export function SimpleTextRenderer({ content, className = '' }: { content: string; className?: string }) {
  return (
    <RichTextRenderer 
      content={content} 
      className={className} 
      parseHtml={true} 
      parseMarkdown={false} 
    />
  );
}

/**
 * 完整的Markdown渲染器
 * 支持所有Markdown语法和HTML标签
 */
export function MarkdownRenderer({ content, className = '' }: { content: string; className?: string }) {
  return (
    <RichTextRenderer 
      content={content} 
      className={className} 
      parseHtml={true} 
      parseMarkdown={true} 
    />
  );
}
